import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Col, message, Result, Row, Table } from "antd";
import {
  WalletOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  BankOutlined,
  RiseOutlined,
  FallOutlined,
  TeamOutlined,
  SolutionOutlined,
  AppstoreOutlined,
  ApartmentOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import Service from "../../service";
import { hideAuthLoader, showAuthLoader } from "../../appRedux/actions/Auth";
import {
  expenseApi,
  formatMoney,
  resolveDashboardScope,
  statusTag,
} from "./expenseHelpers";
import { getResource } from "../../util/getResourcePermission";
import config from "../../permission/config.json";
import NodataFoundIcon from "../../assets/images/NodataFoundIcon";
import "./expenseManagement.css";
import { PAGE_SIZE_OPTIONS } from "../../constants/tablePagination";
import EmpNameCodeCell from "../common/EmpNameCodeCell";
import { QuestionCircleOutlined } from "@ant-design/icons";
import GuidedTour, { hasTourFlag } from "../common/GuidedTour/GuidedTour";

const TOUR_STORAGE_KEY = "tour_seen_expense_dashboard";
const TOUR_HINT_STORAGE_KEY = "tour_hint_dismissed_expense_dashboard";

// Inline titled header for breakdown / list section cards.
const SectionTitle = ({ icon, children }) => (
  <span className="expense-section-title-inline">
    {icon}
    <span>{children}</span>
  </span>
);

// Single KPI tile - polished icon + title + value, uniform across scopes.
const StatTile = ({ title, value, icon, accent, valueClassName }) => (
  <div className={`expense-stat-tile ${accent || ""}`}>
    <div className="expense-stat-tile-info">
      <span className="expense-stat-tile-title">{title}</span>
      <span className={`expense-stat-tile-value ${valueClassName || ""}`}>
        {formatMoney(value)}
      </span>
    </div>
    <div className="expense-stat-tile-icon">{icon}</div>
  </div>
);

// Metric tile grid used by all three dashboard scopes.
const MetricGrid = ({ metrics }) => (
  <div className="expense-stat-grid">
    <StatTile
      title="Advances Received"
      value={metrics.totalAdvancesReceived}
      icon={<WalletOutlined />}
      accent="tile-blue"
    />
    <StatTile
      title="Expenses Submitted"
      value={metrics.expensesSubmitted}
      icon={<FileTextOutlined />}
      accent="tile-blue"
    />
    <StatTile
      title="Pending Approval"
      value={metrics.pendingApproval?.amount}
      icon={<ClockCircleOutlined />}
      accent="tile-orange"
    />
    <StatTile
      title="Approved"
      value={metrics.approvedExpenses}
      icon={<CheckCircleOutlined />}
      accent="tile-green"
    />
    <StatTile
      title="Rejected"
      value={metrics.rejectedExpenses}
      icon={<CloseCircleOutlined />}
      accent="tile-red"
    />
    <StatTile
      title="Remaining Balance"
      value={metrics.remainingAdvanceBalance}
      icon={<BankOutlined />}
      accent="tile-cyan"
    />
    <StatTile
      title="Reimbursement Due"
      value={metrics.reimbursementDue}
      icon={<RiseOutlined />}
      accent="tile-red"
      valueClassName="expense-stat-negative"
    />
    <StatTile
      title="Amount To Return"
      value={metrics.amountToReturn}
      icon={<FallOutlined />}
      accent="tile-green"
      valueClassName="expense-stat-positive"
    />
  </div>
);

const ExpenseDashboard = () => {
  const dispatch = useDispatch();
  const [scope] = useState(resolveDashboardScope);
  const [data, setData] = useState(null);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [isTourHintOpen, setIsTourHintOpen] = useState(false);

  useEffect(() => {
    if (!hasTourFlag(TOUR_STORAGE_KEY) && !hasTourFlag(TOUR_HINT_STORAGE_KEY)) {
      setIsTourHintOpen(true);
    }
  }, []);

  const hintSteps = useMemo(
    () => [
      {
        target: '[data-tour="exp-dash-guide-icon"]',
        title: "New here?",
        content: "Click here for a quick guide to this dashboard.",
        shape: "circle",
      },
    ],
    []
  );

  const employeeTourSteps = useMemo(
    () => [
      {
        target: '[data-tour="exp-dash-metrics"]',
        title: "Your Numbers",
        content:
          "A quick snapshot - advances received, what you have submitted, what is pending approval, approved, rejected, remaining advance balance, reimbursement due to you and any amount you still owe back.",
      },
      {
        target: '[data-tour="exp-dash-advances"]',
        title: "Recent Advances",
        content: "Your latest advance requests and their status.",
      },
      {
        target: '[data-tour="exp-dash-expenses"]',
        title: "Recent Expenses",
        content: "Your latest submitted expenses and their approval status.",
      },
    ],
    []
  );

  const managerTourSteps = useMemo(
    () => [
      {
        target: '[data-tour="exp-dash-metrics"]',
        title: "Team Totals",
        content: "Combined advances, submissions, approvals and balances across your whole team.",
      },
      {
        target: '[data-tour="exp-dash-team"]',
        title: "Team Members",
        content:
          "Per-employee breakdown - advances received, submitted, pending, approved and remaining balance for each team member.",
      },
    ],
    []
  );

  const financeTourSteps = useMemo(
    () => [
      {
        target: '[data-tour="exp-dash-metrics"]',
        title: "Org-wide Totals",
        content: "Company-wide advances, submissions, approvals and balances.",
      },
      {
        target: '[data-tour="exp-dash-status"]',
        title: "By Status",
        content: "How much is sitting in each approval status across the company.",
      },
      {
        target: '[data-tour="exp-dash-category"]',
        title: "By Category",
        content: "Spend broken down by expense category.",
      },
      {
        target: '[data-tour="exp-dash-department"]',
        title: "By Department",
        content: "Spend broken down by department.",
      },
      {
        target: '[data-tour="exp-dash-outstanding"]',
        title: "Advance Outstanding",
        content: "Advances that are still not fully settled, per employee.",
      },
    ],
    []
  );

  const guideIcon = (
    <QuestionCircleOutlined
      data-tour="exp-dash-guide-icon"
      className="guide-title-icon"
      role="button"
      tabIndex={0}
      onClick={() => setIsTourOpen(true)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") setIsTourOpen(true);
      }}
      title="Click for a step-by-step guide"
    />
  );

  const renderTours = (steps) => (
    <>
      {isTourOpen && (
        <GuidedTour steps={steps} onClose={() => setIsTourOpen(false)} storageKey={TOUR_STORAGE_KEY} />
      )}
      {!isTourOpen && isTourHintOpen && (
        <GuidedTour steps={hintSteps} onClose={() => setIsTourHintOpen(false)} storageKey={TOUR_HINT_STORAGE_KEY} />
      )}
    </>
  );

  const canRead = useMemo(
    () => getResource(config.RESOURCES.EXPENSE_DASHBOARD, ["read"]),
    []
  );

  const endpoint = {
    employee: Service.expenseDashboardEmployee,
    manager: Service.expenseDashboardManager,
    finance: Service.expenseDashboardFinance,
  }[scope];

  const load = useCallback(async () => {
    dispatch(showAuthLoader());
    try {
      const result = await expenseApi.get(endpoint);
      setData(result);
    } catch (err) {
      message.error(err.message || "Failed to load dashboard");
    } finally {
      dispatch(hideAuthLoader());
    }
  }, [dispatch, endpoint]);

  useEffect(() => {
    if (canRead) load();
  }, [canRead, load]);

  if (!canRead) {
    return (
      <Card className="employee-card">
        <Result
          status="403"
          title="Access Denied"
          subTitle="You do not have permission to view the expense dashboard."
        />
      </Card>
    );
  }

  if (!data) return null;

  // ── Employee scope ────────────────────────────────────────────────────────
  if (scope === "employee") {
    return (
      <Card className="employee-card expense-page">
        <div className="inout-header text-left">
          <h2>My Expense Dashboard{" "}{guideIcon}</h2>
        </div>
        <Card className="main-content-wrapper">
        <div data-tour="exp-dash-metrics">
          <MetricGrid metrics={data} />
        </div>
        <Row gutter={[16, 16]} align="stretch" className="expense-dashboard-section">
          <Col xs={24} lg={12}>
            <Card data-tour="exp-dash-advances" title={<SectionTitle icon={<WalletOutlined />}>Recent Advances</SectionTitle>} className="expense-table-card expense-section-card" bordered={false}>
              <div className="main-table-wrapper">
                <Table
                  rowKey={(r) => r._id || r.advanceId}
                  size="small"
                  pagination={false}
                  locale={{ emptyText: <NodataFoundIcon width={64} height={52} /> }}
                  dataSource={data.recentAdvances || []}
                  columns={[
                    { title: "ID", dataIndex: "advanceId" },
                    { title: "Amount", dataIndex: "advanceAmount", render: (v) => formatMoney(v) },
                    { title: "Status", dataIndex: "status", render: (v) => statusTag(v) },
                  ]}
                />
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card data-tour="exp-dash-expenses" title={<SectionTitle icon={<FileTextOutlined />}>Recent Expenses</SectionTitle>} className="expense-table-card expense-section-card" bordered={false}>
              <div className="main-table-wrapper">
                <Table
                  rowKey={(r) => r._id}
                  size="small"
                  pagination={false}
                  locale={{ emptyText: <NodataFoundIcon width={64} height={52} /> }}
                  dataSource={data.recentExpenses || []}
                  columns={[
                    { title: "Category", dataIndex: "category" },
                    { title: "Amount", dataIndex: "amount", render: (v) => formatMoney(v) },
                    { title: "Status", dataIndex: "approvalStatus", render: (v) => statusTag(v) },
                  ]}
                />
              </div>
            </Card>
          </Col>
        </Row>
        </Card>
        {renderTours(employeeTourSteps)}
      </Card>
    );
  }

  // ── Manager (team) scope ──────────────────────────────────────────────────
  if (scope === "manager") {
    return (
      <Card className="employee-card expense-page">
        <div className="inout-header text-left">
          <h2>Team Expense Dashboard (Team size: {data.teamSize || 0}){" "}{guideIcon}</h2>
        </div>
        <Card className="main-content-wrapper">
        <div data-tour="exp-dash-metrics">
          <MetricGrid metrics={data.teamTotals || {}} />
        </div>
        <div className="expense-dashboard-section">
          <Card data-tour="exp-dash-team" title={<SectionTitle icon={<TeamOutlined />}>Team Members</SectionTitle>} className="expense-table-card expense-section-card" bordered={false}>
            <div className="main-table-wrapper">
              <Table
                rowKey={(r) => r.employeeId}
                dataSource={data.teamMembers || []}
                locale={{ emptyText: <NodataFoundIcon width={64} height={52} /> }}
                pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: PAGE_SIZE_OPTIONS }}
                columns={[
                  { title: "Employee", dataIndex: "name", render: (v, r) => <EmpNameCodeCell name={v} code={r.empCode} /> },
                  { title: "Advances", dataIndex: "advancesReceived", render: (v) => formatMoney(v) },
                  { title: "Submitted", dataIndex: "submitted", render: (v) => formatMoney(v) },
                  { title: "Pending", dataIndex: "pending", render: (v) => formatMoney(v) },
                  { title: "Approved", dataIndex: "approved", render: (v) => formatMoney(v) },
                  { title: "Remaining", dataIndex: "remaining", render: (v) => formatMoney(v) },
                ]}
                scroll={{ x: true }}
              />
            </div>
          </Card>
        </div>
        </Card>
        {renderTours(managerTourSteps)}
      </Card>
    );
  }

  // ── Finance (org-wide) scope ──────────────────────────────────────────────
  return (
    <Card className="employee-card expense-page">
      <div className="inout-header text-left">
        <h2>Finance Expense Dashboard{" "}{guideIcon}</h2>
      </div>
      <Card className="main-content-wrapper">
      <div data-tour="exp-dash-metrics">
        <MetricGrid metrics={data.orgTotals || {}} />
      </div>
      <Row gutter={[16, 16]} align="stretch" className="expense-dashboard-section">
        <Col xs={24} lg={12}>
          <Card data-tour="exp-dash-status" title={<SectionTitle>By Status</SectionTitle>} className="expense-table-card expense-section-card" bordered={false}>
            <div className="main-table-wrapper" style={{ paddingTop: "0px" }}>
              <Table
                rowKey={(r) => r._id || r.status}
                size="small"
                pagination={false}
                locale={{ emptyText: <NodataFoundIcon width={64} height={52} /> }}
                dataSource={data.byStatus || []}
                columns={[
                  { title: "Status", dataIndex: "status", render: (v) => statusTag(v) },
                  { title: "Count", dataIndex: "count" },
                  { title: "Amount", dataIndex: "amount", render: (v) => formatMoney(v) },
                ]}
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card data-tour="exp-dash-category" title={<SectionTitle>By Category</SectionTitle>} className="expense-table-card expense-section-card" bordered={false}>
            <div className="main-table-wrapper" style={{ paddingTop: "0px" }}>
              <Table
                rowKey={(r) => r._id || r.category}
                size="small"
                pagination={false}
                locale={{ emptyText: <NodataFoundIcon width={64} height={52} /> }}
                dataSource={data.byCategory || []}
                columns={[
                  { title: "Category", dataIndex: "category", render: (v) => v || "-" },
                  { title: "Amount", dataIndex: "amount", render: (v) => formatMoney(v) },
                ]}
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card data-tour="exp-dash-department" title={<SectionTitle>By Department</SectionTitle>} className="expense-table-card expense-section-card" bordered={false}>
            <div className="main-table-wrapper" style={{ paddingTop: "0px" }}>
              <Table
                rowKey={(r) => r._id || r.department}
                size="small"
                pagination={false}
                locale={{ emptyText: <NodataFoundIcon width={64} height={52} /> }}
                dataSource={data.byDepartment || []}
                columns={[
                  { title: "Department", dataIndex: "departmentName", render: (v) => v || "-" },
                  { title: "Amount", dataIndex: "amount", render: (v) => formatMoney(v) },
                ]}
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card data-tour="exp-dash-outstanding" title={<SectionTitle>Advance Outstanding</SectionTitle>} className="expense-table-card expense-section-card" bordered={false}>
            <div className="main-table-wrapper" style={{ paddingTop: "0px" }}>
              <Table
                rowKey={(r) => r._id || r.advanceId}
                size="small"
                pagination={false}
                locale={{ emptyText: <NodataFoundIcon width={64} height={52} /> }}
                dataSource={
                  Array.isArray(data.advanceOutstanding)
                    ? data.advanceOutstanding
                    : data.advanceOutstanding?.advances || []
                }
                columns={[
                  { title: "Advance", dataIndex: "advanceId", render: (v) => v || "-" },
                  { title: "Employee", dataIndex: "name", render: (v, r) => <EmpNameCodeCell name={v} code={r.empCode} /> },
                  { title: "Outstanding", dataIndex: "remaining", render: (v) => formatMoney(v) },
                ]}
              />
            </div>
          </Card>
        </Col>
      </Row>
      </Card>
      {renderTours(financeTourSteps)}
    </Card>
  );
};

export default ExpenseDashboard;
